import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import GoogleCallback from './pages/GoogleCallback';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Drilldown from './pages/Drilldown';
import Budgets from './pages/Budgets';
import Subscriptions from './pages/Subscriptions';
import RecurringBills from './pages/RecurringBills';
import Accounts from './pages/Accounts';
import Categories from './pages/Categories';
import Assets from './pages/Assets';
import AssetExpenses from './pages/AssetExpenses';
import Reconciliation from './pages/Reconciliation';
import BankFeeds from './pages/BankFeeds';
import PlaidOAuth from './pages/PlaidOAuth';
import CategoryReview from './pages/CategoryReview';
import Tasks from './pages/Tasks';
import AccountsReceivable from './pages/AccountsReceivable';
import AccountsPayable from './pages/AccountsPayable';
import Inventory from './pages/Inventory';
import UserSettings from './pages/UserSettings';
import Customers from './pages/Customers';
import Vehicles from './pages/Vehicles';
import Properties from './pages/Properties';
import Creditors from './pages/Creditors';
import Business from './pages/Business';
import Staff from './pages/Staff';
import Payroll from './pages/Payroll';
import ResolutionLetters from './pages/ResolutionLetters';
import Schedules from './pages/Schedules';
import Reports from './pages/Reports';
import AdminUsers from './pages/AdminUsers';
import AdminDashboard from './pages/AdminDashboard';
import AdminSecurity from './pages/AdminSecurity';
import ErrorReports from './pages/ErrorReports';
import SupportMessages from './pages/SupportMessages';
import UserBehaviorAnalytics from './pages/UserBehaviorAnalytics';
import Billing from './pages/Billing';
import Onboarding from './pages/Onboarding';
import Support from './pages/Support';
import Contact from './pages/Contact';
import Legal from './pages/Legal';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Security from './pages/Security';
import PortalApp from './features/client-portal/PortalApp';
import PayInvoice from './pages/PayInvoice';
import BuyItem from './pages/BuyItem';
import { reportFrontendError } from './utils/reportError';
import { useUserBehaviorTracking } from './hooks/useUserBehaviorTracking';

// Configure React Router future flags to suppress warnings
const routerFutureConfig = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

// Create React Query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      retry: 1, // Retry failed requests once
      refetchOnMount: true, // Refetch when component mounts (but use cache if fresh)
    },
  },
});

// '/' for a signed-in person goes to the dashboard. While the session check
// runs (hard reload), show the loading screen, not the public landing page.
export const SignedOutOnly = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }
  return children;
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const GlobalErrorFallback = ({ resetErrorBoundary }) => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-100 text-center px-4">
    <h1 className="text-2xl font-semibold text-gray-800">Something went wrong</h1>
    <p className="text-gray-600 max-w-md">We hit an unexpected issue while loading the application. Try again and contact support if the problem continues.</p>
    <div className="flex gap-3">
      <button
        type="button"
        className="rounded-md bg-blue-600 px-4 py-2 text-white shadow-sm hover:bg-blue-700 focus:outline-none focus-visible:ring"
        onClick={() => {
          resetErrorBoundary();
          window.location.reload();
        }}
      >
        Reload
      </button>
      <button
        type="button"
        className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-white focus:outline-none focus-visible:ring"
        onClick={() => resetErrorBoundary()}
      >
        Try Again
      </button>
    </div>
  </div>
);

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  
  // Initialize user behavior tracking (automatically tracks all page changes)
  useUserBehaviorTracking();

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/" 
        element={<SignedOutOnly><Landing /></SignedOutOnly>} 
      />
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
      />
      <Route path="/portal/*" element={<PortalApp />} />
      <Route path="/p/:slug/buy/:item" element={<BuyItem />} />
      <Route path="/p/:slug/*" element={<PortalApp />} />
      <Route path="/pay/:token" element={<PayInvoice />} />
      <Route
        path="/pricing"
        element={<Pricing />}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />}
      />
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      <Route
        path="/auth/google/callback"
        element={<GoogleCallback />}
      />
      <Route 
        path="/support" 
        element={<Support />} 
      />
      <Route 
        path="/contact" 
        element={<Contact />} 
      />
      <Route 
        path="/legal" 
        element={<Legal />} 
      />
      <Route 
        path="/legal/privacy-policy" 
        element={<PrivacyPolicy />} 
      />
      <Route 
        path="/legal/terms-of-service" 
        element={<TermsOfService />} 
      />
      <Route 
        path="/legal/security" 
        element={<Security />} 
      />
      
      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route path="/drilldown" element={<ProtectedRoute><Layout><Drilldown /></Layout></ProtectedRoute>} />
      <Route 
        path="/transactions" 
        element={
          <ProtectedRoute>
            <Layout><Transactions /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/budgets" 
        element={
          <ProtectedRoute>
            <Layout><Budgets /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/categories" 
        element={
          <ProtectedRoute>
            <Layout><Categories /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <Layout><Subscriptions /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Layout><Billing /></Layout>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/recurring-bills" 
        element={
          <ProtectedRoute>
            <Layout><RecurringBills /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accounts" 
        element={
          <ProtectedRoute>
            <Layout><Accounts /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/assets" 
        element={
          <ProtectedRoute>
            <Layout><Assets /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/asset-expenses" 
        element={
          <ProtectedRoute>
            <Layout><AssetExpenses /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customers" 
        element={
          <ProtectedRoute>
            <Layout><Customers /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/accounts-receivable"
        element={
          <ProtectedRoute>
            <Layout><AccountsReceivable documentType="invoice" /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            <Layout><AccountsReceivable documentType="quote" /></Layout>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/accounts-payable" 
        element={
          <ProtectedRoute>
            <Layout><AccountsPayable /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute>
            <Layout><Inventory /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Layout><UserSettings /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vehicles" 
        element={
          <ProtectedRoute>
            <Layout><Vehicles /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/properties" 
        element={
          <ProtectedRoute>
            <Layout><Properties /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/creditors" 
        element={
          <ProtectedRoute>
            <Layout><Creditors /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/business" 
        element={
          <ProtectedRoute>
            <Layout><Business /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/staff" 
        element={
          <ProtectedRoute>
            <Layout><Staff /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payroll" 
        element={
          <ProtectedRoute>
            <Layout><Payroll /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/resolution-letters" 
        element={
          <ProtectedRoute>
            <Layout><ResolutionLetters /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/schedules" 
        element={
          <ProtectedRoute>
            <Layout><Schedules /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/category-review"
        element={
          <ProtectedRoute>
            <Layout><CategoryReview /></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/plaid/oauth" element={<ProtectedRoute><Layout><PlaidOAuth /></Layout></ProtectedRoute>} />
      <Route 
        path="/bank-feeds" 
        element={
          <ProtectedRoute>
            <Layout><BankFeeds /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reconciliation" 
        element={
          <ProtectedRoute>
            <Layout><Reconciliation /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks" 
        element={
          <ProtectedRoute>
            <Layout><Tasks /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <Layout><Reports /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute>
            <Layout><AdminDashboard /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <Layout><AdminUsers /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/security"
        element={
          <ProtectedRoute>
            <Layout><AdminSecurity /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/error-reports"
        element={
          <ProtectedRoute>
            <Layout><ErrorReports /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support-messages"
        element={
          <ProtectedRoute>
            <Layout><SupportMessages /></Layout>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/analytics" 
        element={
          <ProtectedRoute>
            <Layout><UserBehaviorAnalytics /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary fallback={GlobalErrorFallback} onError={reportFrontendError}>
      <QueryClientProvider client={queryClient}>
        <Router future={routerFutureConfig}>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
